import React from 'react';
import { motion } from 'motion/react';
import { Icons } from '@/src/modules/components/Icons';
import { Footer } from '@/src/modules/components/Footer';

interface Feature {
  icon: React.ReactNode;
  name: string;
  description: string;
  upcoming?: boolean;
}

const invitationFeatures: Feature[] = [
  { icon: <Icons.FileText size={24} />, name: 'Digital Invitations', description: 'Create stunning invitations with customizable templates' },
  { icon: <Icons.CheckCircle size={24} />, name: 'RSVP Tracking', description: 'Track acceptances and manage guest responses in real-time' },
  { icon: <Icons.Users size={24} />, name: 'Guest Management', description: 'Organize and manage your guest lists effortlessly' },
  { icon: <Icons.Calendar size={24} />, name: 'Event Scheduling', description: 'Set dates and automate reminders for your events' },
];

const interactiveFeatures: Feature[] = [
  { icon: <Icons.Wedding size={24} />, name: 'Blessings Wall', description: 'Collect beautiful messages and blessings from guests', upcoming: true },
  { icon: <Icons.Gift size={24} />, name: 'Memory Capsule', description: 'Preserve precious memories from your celebration', upcoming: true },
  { icon: <Icons.Camera size={24} />, name: 'Media Gallery', description: 'Share photos and memories with your guests' },
  { icon: <Icons.Chat size={24} />, name: 'Guest Messages', description: 'Enable guest interactions and engagement' },
];

const sharingFeatures: Feature[] = [
  { icon: <Icons.WhatsApp size={24} />, name: 'WhatsApp Sharing', description: 'Share invitations directly via WhatsApp' },
  { icon: <Icons.Sparkles size={24} />, name: 'QR Sharing', description: 'Generate scannable QR codes for instant access', upcoming: true },
  { icon: <Icons.Link size={24} />, name: 'Smart Links', description: 'Create trackable and shareable invitation links' },
  { icon: <Icons.Corporate size={24} />, name: 'Partner Profiles', description: 'Showcase partner and vendor branding' },
  { icon: <Icons.Analytics size={24} />, name: 'Analytics', description: 'View detailed invitation performance metrics' },
  { icon: <Icons.Palette size={24} />, name: 'Watermark Branding', description: 'Add custom branding to invitations', upcoming: true },
];

function FeatureCard({ feature }: { feature: Feature }) {
  return (
    <motion.div
      whileHover={{ y: -4 }}
      className="group p-6 rounded-2xl bg-surface-low border border-outline-variant/30 hover:border-primary/40 transition-all"
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center group-hover:scale-110 transition-transform flex-shrink-0">
          {feature.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="font-bold text-on-surface">{feature.name}</h3>
            {feature.upcoming && (
              <span className="text-[10px] font-bold uppercase bg-status-processing/20 text-status-processing px-2 py-0.5 rounded-full">
                Coming Soon
              </span>
            )}
          </div>
          <p className="text-sm text-on-surface-variant">{feature.description}</p>
        </div>
      </div>
    </motion.div>
  );
}

export function Features() {
  return (
    <div className="flex flex-col w-full bg-surface-lowest">
      {/* Hero */}
      <section className="relative pt-20 pb-16 lg:pt-28 lg:pb-24 overflow-hidden">
        <div className="absolute top-0 right-0 -mr-[15%] -mt-[10%] w-[50%] h-[70%] rounded-full bg-primary/15 blur-[100px] pointer-events-none" />

        <div className="max-w-4xl mx-auto px-6 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl lg:text-6xl font-bold text-on-surface mb-6 leading-tight">
              Powerful features for <span className="text-primary">every celebration</span>
            </h1>
            <p className="text-lg text-on-surface-variant max-w-2xl mx-auto">
              Everything you need to create, share, and manage invitations with confidence. From digital delivery to guest engagement.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Invitation Features */}
      <section className="py-20 border-b border-outline-variant/20">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-3xl font-bold text-on-surface mb-2">Invitation Features</h2>
            <p className="text-on-surface-variant">Create and manage invitations like never before</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {invitationFeatures.map((feature, idx) => (
              <motion.div
                key={feature.name}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                viewport={{ once: true }}
              >
                <FeatureCard feature={feature} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Features */}
      <section className="py-20 border-b border-outline-variant/20">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-3xl font-bold text-on-surface mb-2">Interactive Features</h2>
            <p className="text-on-surface-variant">Engage your guests with interactive elements</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {interactiveFeatures.map((feature, idx) => (
              <motion.div
                key={feature.name}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                viewport={{ once: true }}
              >
                <FeatureCard feature={feature} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sharing & Partner Features */}
      <section className="py-20 border-b border-outline-variant/20">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-3xl font-bold text-on-surface mb-2">Sharing & Partner Features</h2>
            <p className="text-on-surface-variant">Multi-channel sharing and professional branding</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sharingFeatures.map((feature, idx) => (
              <motion.div
                key={feature.name}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                viewport={{ once: true }}
              >
                <FeatureCard feature={feature} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
